<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import { Stack, Text } from '@Shelly/shelly-design-system';
	import type { FlowNode } from '../../types/flow-types';
	import BaseInspector from '../BaseInspector.svelte';
	import InlineDeviceSelector from '../Shared/InlineDeviceSelector.svelte';
	import ChannelSelector from '../Shared/ChannelSelector.svelte';
	import DeviceConditionEditor from './DeviceConditionEditor.svelte';
	import TokenSelector from '../Shared/TokenSelector.svelte';
	import { devices } from '../../../../stores';
	import {
		getDeviceSettings,
		getDeviceStatus,
		readDeviceSettings,
		readDeviceStatus,
	} from '../../../../stores/device-runtime/status-selectors';
	import { get } from 'svelte/store';
	import { generateConditionLabel } from '../helpers/LabelGenerator';
	import { real_id } from '../../../../helpers/interface_helpers';
	import { resolveDeviceInStore } from '../../helpers/resolveDeviceInStore';
	import { _init_conditions_components } from '../../../Scenes/SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/common-helpers';
	import { defaultDeviceCondition } from '../../../Scenes/SceneWizard/Process/When/AddEditContionWizard/helpers/when-wizard-helpers';
	import { _deepClone } from '../../../../helpers/utilities';
	import { toSceneOperator } from '../../converters/helpers/device-conditions';
	import { isDeviceCheckPalette } from '../../nodes/definitions/conditions/device-condition';
	import {
		deviceConditionInterpretator,
		deviceConditionSecondFormat,
	} from '../../../Scenes/helpers/scene_conditions_to_ui';

	export let node: FlowNode;

	const dispatch = createEventDispatcher<{ update: FlowNode }>();

	// ── Local state ────────────────────────────────────────────────────────────

	let selectedDevice: string | null = null;
	let selectedChannel: number = 0;
	let executionMode: 'once' | 'any' | 'repeat' = 'any';
	let holdOff: number = 5;

	/**
	 * Condition component descriptors for the selected device.
	 * Populated imperatively via get() so we never reactively subscribe
	 * to $devices_status or $devices_settings — that was the root cause
	 * of the original freeze (DevicePropsStep wrote to sceneData on every
	 * store tick, triggering an endless dispatch loop).
	 */
	let components: any[] = [];

	/** Current _device_condition object, kept in sync with node.data. */
	let condition: any = _deepClone(defaultDeviceCondition);

	// ── Initialization guard ───────────────────────────────────────────────────

	/**
	 * Only re-initialize when a DIFFERENT node is selected.
	 * Without this guard, dispatching an update rewrites `node` (same id,
	 * new object reference) → initializeFromNode fires → dispatch → infinite loop.
	 */
	let lastInitializedNodeId = '';
	/** Reload condition components when devices store catches up after init. */
	let deferredDeviceId: string | null = null;

	$: if (node && node.id !== lastInitializedNodeId) {
		lastInitializedNodeId = node.id;
		initializeFromNode();
	}

	function normalizeConditionFromNode(): any | null {
		const dc = (node.data._device_condition || {}) as any;
		const cap = (node.data.sceneCapability || {}) as any;
		const topDevice = node.data.device as any;
		const topProperty = node.data.property as any;
		let parsedScene: any = {};
		if (node.data.sceneData) {
			try {
				const rawScene = node.data.sceneData as any;
				parsedScene =
					typeof rawScene.r === 'string' && rawScene.cmpop !== undefined
						? deviceConditionSecondFormat(rawScene)
						: deviceConditionInterpretator(rawScene);
			} catch {
				parsedScene = {};
			}
		}
		const deviceId =
			dc.device?.id ??
			dc.deviceId ??
			cap.device?.id ??
			cap.deviceId ??
			parsedScene.device?.id ??
			topDevice?.id;
		if (!deviceId) return null;

		const channel =
			dc.device?.channel ??
			dc.channel ??
			cap.device?.channel ??
			cap.channel ??
			parsedScene.device?.channel ??
			topDevice?.channel ??
			'0';
		const condName =
			dc.cond_name ??
			cap.cond_name ??
			cap.capabilityName ??
			cap.name ??
			parsedScene.cond_name ??
			node.data.cond_name ??
			(typeof topProperty === 'string' ? topProperty : topProperty?.id);
		const operator =
			dc.operator ??
			dc.cmpop ??
			cap.operator ??
			cap.cmpop ??
			parsedScene.operator ??
			node.data.operator ??
			node.data.cmpop ??
			'';
		const value =
			dc.value ??
			dc.cmpval ??
			cap.value ??
			cap.cmpval ??
			parsedScene.value ??
			node.data.value ??
			node.data.cmpval ??
			'';

		return {
			..._deepClone(defaultDeviceCondition),
			..._deepClone(dc),
			device: {
				id: real_id(String(deviceId)),
				channel: String(channel),
			},
			cond_name: condName || '',
			add_info_ind: dc.add_info_ind ?? parsedScene.add_info_ind,
			input_index: dc.input_index ?? parsedScene.input_index,
			operator,
			value,
			cmpop: dc.cmpop ?? node.data.cmpop ?? toSceneOperator(String(operator)),
			cmpval: dc.cmpval ?? node.data.cmpval ?? value,
		};
	}

	function initializeFromNode() {
		const condData = normalizeConditionFromNode();
		if (isDeviceCheckPalette(node)) {
			executionMode = ((node.data.executionMode as any) || 'any') as 'once' | 'any' | 'repeat';
			holdOff = Number(node.data.hold_off ?? 5);
		} else {
			executionMode = 'any';
			holdOff = Number(node.data.hold_off ?? 5);
		}

		if (condData?.device?.id) {
			selectedDevice = condData.device.id;
			selectedChannel = parseInt(String(condData.device.channel ?? '0')) || 0;
			condition = _deepClone(condData);
			// Ensure device block has the canonical shape
			condition.device = {
				id: real_id(selectedDevice!),
				channel: String(selectedChannel),
			};
			loadComponents(selectedDevice!);
			deferredDeviceId =
				selectedDevice && !resolveDeviceInStore(get(devices), selectedDevice, selectedChannel)
					? selectedDevice
					: null;
		} else {
			selectedDevice = null;
			selectedChannel = 0;
			condition = _deepClone(defaultDeviceCondition);
			components = [];
			deferredDeviceId = null;
		}
	}

	$: if (
		deferredDeviceId &&
		resolveDeviceInStore($devices, deferredDeviceId, selectedChannel) &&
		node?.id === lastInitializedNodeId
	) {
		loadComponents(deferredDeviceId);
		deferredDeviceId = null;
	}

	/** Load condition components for a device using one-time store reads (no subscription). */
	function loadComponents(deviceId: string) {
		const devicesMap = get(devices);
		const device = resolveDeviceInStore(devicesMap, deviceId, selectedChannel);
		if (!device) {
			components = [];
			return;
		}
		const statusKey = real_id(device.id ?? deviceId);
		const status = readDeviceStatus(statusKey);
		const settings = readDeviceSettings(statusKey);
		try {
			components = _init_conditions_components(device, status, settings);
		} catch (e) {
			console.warn('[ConditionInspector] Failed to init condition components:', e);
			components = [];
		}
	}

	// ── Derived ────────────────────────────────────────────────────────────────

	$: currentDevice = selectedDevice
		? (resolveDeviceInStore($devices, selectedDevice, selectedChannel) as any)
		: null;
	$: selectedStatusStore = getDeviceStatus(selectedDevice || undefined);
	$: selectedSettingsStore = getDeviceSettings(selectedDevice || undefined);
	$: if (selectedDevice && currentDevice && node?.id === lastInitializedNodeId) {
		// Subscribe only to this device's runtime entries. When status/settings hydrate
		// after the graph, rebuild capabilities so properties such as Consumption appear.
		$selectedStatusStore;
		$selectedSettingsStore;
		loadComponents(selectedDevice);
	}
	$: availableChannels = (currentDevice as any)?.channels || [0];
	$: hasMultipleChannels = availableChannels.length > 1;

	$: showTriggerBehavior = isDeviceCheckPalette(node);

	const triggerTypeOptions = [
		{ value: 'any', title: 'On any change (default)' },
		{ value: 'repeat', title: 'Poll continuously' },
		{ value: 'once', title: 'Trigger once (threshold)' },
	];

	// ── Event handlers ─────────────────────────────────────────────────────────

	function handleDeviceSelect(event: CustomEvent) {
		const deviceRef = event.detail;
		if (!deviceRef) {
			selectedDevice = null;
			selectedChannel = 0;
			condition = _deepClone(defaultDeviceCondition);
			components = [];
			commitUpdate();
			return;
		}

		const newDeviceId: string = deviceRef.id || deviceRef;
		selectedDevice = newDeviceId;
		selectedChannel = 0;

		condition = {
			..._deepClone(defaultDeviceCondition),
			device: { id: real_id(newDeviceId), channel: '0' },
		};
		loadComponents(newDeviceId);
		commitUpdate();
	}

	function handleChannelChange(event: CustomEvent) {
		selectedChannel = event.detail as number;
		if (selectedDevice) {
			condition = {
				..._deepClone(defaultDeviceCondition),
				device: { id: real_id(selectedDevice), channel: String(selectedChannel) },
			};
			loadComponents(selectedDevice);
			commitUpdate();
		}
	}

	/**
	 * Receives the full updated _device_condition from DeviceConditionEditor.
	 * DeviceConditionEditor emits on user interaction only (no store subscriptions),
	 * so this will never fire in a tight loop.
	 */
	function handleConditionChange(event: CustomEvent) {
		condition = event.detail;
		// Preserve the device reference from current state
		if (selectedDevice && !condition.device?.id) {
			condition.device = { id: real_id(selectedDevice), channel: String(selectedChannel) };
		}
		commitUpdate();
	}

	function selectValue(detail: any): any {
		if (typeof detail === 'string' || typeof detail === 'number') return detail;
		if (detail && typeof detail === 'object' && 'value' in detail) return detail.value;
		return detail;
	}

	function handleExecutionModeChange(event: CustomEvent) {
		executionMode = selectValue(event.detail) as 'once' | 'any' | 'repeat';
		commitUpdate();
	}

	function handleHoldOffInput(event: Event) {
		const target = event.target as HTMLInputElement;
		const parsed = Number(target.value);
		holdOff = Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0;
		commitUpdate();
	}

	/** Align cmpop/cmpval with Scenes IoTS shape from UI `operator` + `value`. */
	function persistableDeviceCondition(c: any) {
		if (!c || typeof c !== 'object') return c;
		const cmpop = toSceneOperator(String(c.operator ?? ''));
		const cmpval = c.value ?? c.cmpval ?? '';
		return { ...c, cmpop, cmpval };
	}

	/** Write current state to node.data and dispatch update. */
	function commitUpdate() {
		// Clear any cached original scene JSON so the serializer rebuilds from current
		// node data (executionMode, property, operator, value) rather than returning
		// the stale snapshot that was loaded from the saved scene.
		node.data.sceneData = undefined;
		node.data.originalCondition = undefined;

		const conditionOut = persistableDeviceCondition(condition);
		condition = conditionOut;

		node.data._device_condition = _deepClone(conditionOut);

		// Keep legacy sceneCapability in sync for converter compatibility
		if (!node.data.sceneCapability) node.data.sceneCapability = {};
		const cap = node.data.sceneCapability as any;
		cap.deviceId = conditionOut?.device?.id;
		cap.channel = conditionOut?.device?.channel;
		cap.operator = conditionOut?.operator;
		cap.capabilityName = conditionOut?.cond_name;
		cap.value = conditionOut?.value;
		if (showTriggerBehavior) {
			node.data.executionMode = executionMode;
			node.data.hold_off = holdOff;
		} else {
			node.data.executionMode = 'any';
		}

		// Sync top-level fields so DeviceConditionNode structured display shows correct text
		const deviceId = conditionOut?.device?.id || cap.deviceId;
		const nameChannel =
			parseInt(String(conditionOut?.device?.channel ?? selectedChannel ?? '0'), 10) || 0;
		node.data.device_name = deviceId
			? resolveDeviceInStore(get(devices), deviceId, nameChannel)?.name || deviceId
			: '';
		node.data.cond_name = conditionOut?.cond_name || cap.capabilityName || '';
		node.data.operator = conditionOut?.operator ?? cap.operator;
		node.data.cmpop = conditionOut.cmpop;
		node.data.value = conditionOut?.value ?? cap.value;
		node.data.cmpval = conditionOut.cmpval;
		node.data.device = conditionOut?.device
			? { ...conditionOut.device }
			: cap.deviceId
				? { id: cap.deviceId, channel: cap.channel }
				: undefined;
		if (node.data.device && deviceId && !(node.data.device as any).name) {
			(node.data.device as any).name = get(devices)[deviceId]?.name || deviceId;
		}

		if (!node.data.isLabelManuallyOverridden) {
			const generatedLabel = generateConditionLabel(node);
			node.data.displayLabel = generatedLabel;
			node.data.label = generatedLabel;
		}

		node = { ...node, data: { ...node.data } };
		dispatch('update', node);
	}
</script>

<div class="condition-inspector">
	<BaseInspector
		{node}
		title={showTriggerBehavior
			? $format('flow_device_check_settings', { default: 'Device Check' })
			: $format('flow_condition_settings', { default: 'Device Condition' })}
		on:update
	>
		<Stack direction="row" gap="m">
			<!-- Device Selection -->
			<InlineDeviceSelector
				whenConditionFilter
				selectedDevice={selectedDevice
					? {
							id: selectedDevice,
							name: currentDevice?.name || '',
							channel: String(selectedChannel),
						}
					: undefined}
				mode="trigger"
				on:select={handleDeviceSelect}
			/>

			<!-- Channel Selection -->
			{#if selectedDevice && hasMultipleChannels}
				<ChannelSelector
					device={currentDevice}
					channels={availableChannels}
					{selectedChannel}
					on:change={handleChannelChange}
				/>
			{/if}

			<!-- Device Condition Editor — show whenever a device is chosen (components may hydrate after store sync) -->
			{#if selectedDevice}
				<DeviceConditionEditor {components} {condition} on:change={handleConditionChange} />
			{/if}

			{#if showTriggerBehavior}
				<div class="trigger-settings">
					<Text variant="body2">Trigger behavior</Text>
					<TokenSelector
						ariaLabel="Trigger behavior"
						label="Mode"
						options={triggerTypeOptions}
						value={executionMode}
						on:change={handleExecutionModeChange}
					/>
					{#if executionMode === 'once'}
						<div class="holdoff-input-wrap">
							<Text variant="body2">Hold-off (seconds)</Text>
							<input
								type="number"
								min="0"
								value={holdOff}
								on:input={handleHoldOffInput}
								aria-label="Hold-off seconds"
							/>
						</div>
						<p class="mode-hint">
							Fires once when the value crosses the threshold, then waits for hold-off
							before it can fire again.
						</p>
					{:else if executionMode === 'repeat'}
						<p class="mode-hint">
							Re-evaluated periodically. Use as a guard: the true branch runs when the
							condition holds.
						</p>
					{:else}
						<p class="mode-hint">
							Fires every time the device reports a matching state change.
						</p>
					{/if}
				</div>
			{/if}
		</Stack>
	</BaseInspector>
</div>

<style>
	.condition-inspector {
		display: flex;
		flex-direction: column;
		gap: 16px;
	}
	.trigger-settings {
		display: flex;
		flex-direction: column;
		gap: 8px;
		padding-top: 8px;
		border-top: 1px solid rgba(255, 255, 255, 0.08);
	}

	.holdoff-input-wrap {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.holdoff-input-wrap input {
		height: 34px;
		border-radius: 8px;
		border: 1px solid rgba(255, 255, 255, 0.2);
		background: rgba(255, 255, 255, 0.06);
		color: #fff;
		padding: 0 10px;
	}

	.mode-hint {
		margin: 0;
		font-size: 0.75rem;
		color: #6b7280;
		line-height: 1.4;
	}
</style>
